import { SELECTION_TYPES } from "@/schemas/genericSelectionSchema";
import { busPassengerGeneralValidations } from "./generalValidations";
import { createBusPassengerCustomValidations } from "./customValidations";

/**
 * Otobüs yolcu seçimi konfigürasyonu
 * i18n desteği ile
 */

// Passenger types for bus
export const BUS_PASSENGER_TYPES = {
  ADULT: "adult",
  YOUNG: "young",
  CHILD: "child",
};

/**
 * Otobüs yolcu seçim konfigürasyonu
 * @param {Object} options - Customization options
 * @param {Number} options.maxPassengers - Maksimum yolcu sayısı
 * @param {Number} options.minPassengers - Minimum yolcu sayısı
 * @param {Object} options.badges - Badge'ler { slug: "badge metni" }
 *   Örnek: { young: "%10 indirim", adult: "Popüler", child: "Ücretsiz" }
 * @returns {Object} Configuration object
 */
export const createBusPassengerConfig = ({
  maxPassengers = 4,
  minPassengers = 1,
  badges = { }, // Default: young'da %10 indirim
} = {}) => {
  const children = [
    {
      type: SELECTION_TYPES.INCREMENT,
      slug: BUS_PASSENGER_TYPES.ADULT,
      labelKey: "bus.passengerAdult",
      label: "Yetişkin",
      descriptionKey: "bus.passengerAdultDesc",
      description: "24 ve üstü",
      min: 0,
      max: maxPassengers,
      value: 0,
      badge: badges?.adult, // Dışardan gelen badge
    },
    {
      type: SELECTION_TYPES.INCREMENT,
      slug: BUS_PASSENGER_TYPES.YOUNG,
      labelKey: "bus.passengerYoung",
      label: "Genç",
      descriptionKey: "bus.passengerYoungDesc",
      description: "12-23 yaş",
      min: 0,
      max: maxPassengers,
      value: 0,
      // Öncelik: 1. Dışardan gelen badge, 2. i18n key
      /*
      badgeKey: badges?.young ? undefined : "bus.passengerDiscount",
      badge: badges?.young || "%10 indirim", // Default fallback
      */

      badge: badges?.young
    },
    {
      type: SELECTION_TYPES.INCREMENT,
      slug: BUS_PASSENGER_TYPES.CHILD,
      labelKey: "bus.passengerChild",
      label: "Çocuk",
      descriptionKey: "bus.passengerChildDesc",
      description: "2-11 yaş",
      min: 0,
      max: maxPassengers,
      value: 0,
      badge: badges?.child, // Dışardan gelen badge
    },
  ];

  return {
    settings: {
      totalSelections: maxPassengers,
      minTotalSelections: minPassengers,
      allowZeroSelections: false,
      // Bus özel genel validasyonları (ayrı dosyadan)
      generalValidations: busPassengerGeneralValidations,

      // field bazında validasyonlar
      customValidations: createBusPassengerCustomValidations(BUS_PASSENGER_TYPES),
    },
    children: [
      {
        type: SELECTION_TYPES.GROUP,
        //labelKey: "bus.passengerTitle",
        //label: "Yolcu Sayısı", // Fallback
        children,
      },
    ],
  };
};

/**
 * Display formatter for bus passengers
 * @param {Object} values - Current values
 * @param {Function} t - Translation function
 * @returns {String} Display text
 */
export const busPassengerDisplayFormatter = (values, t) => {
  const adultCount = values[BUS_PASSENGER_TYPES.ADULT] || 0;
  const youngCount = values[BUS_PASSENGER_TYPES.YOUNG] || 0;
  const childCount = values[BUS_PASSENGER_TYPES.CHILD] || 0;
  
  const total = adultCount + youngCount + childCount;

  if (total === 0) {
    return t ? t("bus.passengerSelect") : "Yolcu Seçin";
  }

  const parts = [];

  // Yetişkin ve genç birlikte göster
  const regularPassengers = adultCount + youngCount + childCount;
  if (regularPassengers > 0) {
    const passengerText = t ? t("common.passengers") : "Yolcu";
    parts.push(`${regularPassengers} ${passengerText}`);
  }

  /*if (childCount > 0) {
    const childText = t ? t("common.child") : "Çocuk";
    parts.push(`${childCount} ${childText}`);
  }*/

  return parts.join(", ");
};

/**
 * Dynamic config creator
 * @param {Object} options - Dynamic options
 * @param {String} options.busType - Bus type (standard, vip, etc.)
 * @param {Number} options.availableSeats - Available seats from route selection
 * @param {Object} options.badges - Badge'ler { slug: "badge metni" }
 *   Örnek: { young: "%15 indirim", adult: "Popüler" }
 * @returns {Object} Configuration object
 */
export const createDynamicBusPassengerConfig = ({
  busType = "standard",
  availableSeats,
  badges = { young: "%10 indirim" }, // Default
} = {}) => {
  let maxPassengers = 4;
  if (availableSeats !== undefined) {
    maxPassengers = Math.min(availableSeats, 4);
  } else if (busType === "vip") {
    maxPassengers = 2;
  }

  return createBusPassengerConfig({
    maxPassengers,
    minPassengers: 1,
    badges, // Badge'leri geç
  });
};

/**
 * Varsayılan config
 */
export const defaultBusPassengerConfig = createBusPassengerConfig();

